import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { 
  BarChart3, Plus, DollarSign, Eye, MousePointer, Target,
  TrendingUp, TrendingDown, Play, Pause, Settings, Filter,
  Facebook, Instagram, Linkedin, Search, MoreVertical,
  Edit2, Trash2, X, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const platforms = [
  { id: 'facebook', name: 'Facebook Ads', icon: Facebook, color: 'text-blue-500' },
  { id: 'instagram', name: 'Instagram Ads', icon: Instagram, color: 'text-pink-500' },
  { id: 'google', name: 'Google Ads', icon: Search, color: 'text-red-400' },
  { id: 'linkedin', name: 'LinkedIn Ads', icon: Linkedin, color: 'text-blue-600' },
];

export default function AdManager() {
  const { token } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newAd, setNewAd] = useState({
    name: '',
    platform: 'facebook',
    type: 'image',
    headline: '',
    description: '',
    cta: 'Learn More',
    targetUrl: '',
    budget: ''
  });

  useEffect(() => {
    fetchAds();
  }, [token]);

  const fetchAds = async () => {
    try {
      const response = await fetch(`${API}/api/marketing/ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAds(data.ads || []);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAds = selectedPlatform === 'all' 
    ? ads 
    : ads.filter(ad => ad.platform === selectedPlatform);

  const getPlatformInfo = (platformId) => {
    return platforms.find(p => p.id === platformId) || platforms[0];
  };

  const toggleAdStatus = async (ad) => {
    const newStatus = ad.status === 'active' ? 'paused' : 'active';
    try {
      const response = await fetch(`${API}/api/marketing/ads/${ad.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setAds(ads.map(a => a.id === ad.id ? { ...a, status: newStatus } : a));
        toast.success(`Ad ${newStatus === 'active' ? 'activated' : 'paused'}`);
      }
    } catch (error) {
      toast.error('Failed to update ad');
    }
  };

  const handleCreateAd = async () => {
    if (!newAd.name || !newAd.headline) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/api/marketing/ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newAd)
      });

      if (response.ok) {
        const data = await response.json();
        setAds([data.ad, ...ads]);
        setNewAd({ name: '', platform: 'facebook', type: 'image', headline: '', description: '', cta: 'Learn More', targetUrl: '', budget: '' });
        setShowCreateModal(false);
        toast.success('Ad created successfully');
      } else {
        toast.error('Failed to create ad');
      }
    } catch (error) {
      toast.error('Error creating ad');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;

    try {
      const response = await fetch(`${API}/api/marketing/ads/${adId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setAds(ads.filter(a => a.id !== adId));
        toast.success('Ad deleted');
      }
    } catch (error) {
      toast.error('Failed to delete ad');
    }
    setShowActionsMenu(null);
  };

  const totalStats = {
    spent: ads.reduce((sum, ad) => sum + (ad.spent || 0), 0),
    impressions: ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0),
    clicks: ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0),
    conversions: ads.reduce((sum, ad) => sum + (ad.conversions || 0), 0),
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Ad Manager
          </h1>
          <p className="text-muted-foreground">Manage and optimize your paid advertising</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="marketing-btn-primary flex items-center gap-2"
          data-testid="create-ad-btn"
        >
          <Plus className="w-4 h-4" />
          <span>Create Ad</span>
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Total Spent</span>
          </div>
          <p className="text-2xl font-bold">${totalStats.spent.toLocaleString()}</p>
        </div>
        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-muted-foreground">Impressions</span>
          </div>
          <p className="text-2xl font-bold">{(totalStats.impressions / 1000).toFixed(0)}K</p>
        </div>
        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <MousePointer className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-muted-foreground">Clicks</span>
          </div>
          <p className="text-2xl font-bold">{(totalStats.clicks / 1000).toFixed(1)}K</p>
        </div>
        <div className="metric-card">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-muted-foreground">Conversions</span>
          </div>
          <p className="text-2xl font-bold">{totalStats.conversions}</p>
        </div>
      </div>

      {/* Platform Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedPlatform('all')}
          className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
            selectedPlatform === 'all' 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-secondary hover:bg-secondary/80'
          }`}
          data-testid="filter-all"
        >
          All Platforms
        </button>
        {platforms.map(platform => (
          <button
            key={platform.id}
            onClick={() => setSelectedPlatform(platform.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
              selectedPlatform === platform.id 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-secondary hover:bg-secondary/80'
            }`}
            data-testid={`filter-${platform.id}`}
          >
            <platform.icon className={`w-4 h-4 ${selectedPlatform !== platform.id ? platform.color : ''}`} />
            <span>{platform.name}</span>
          </button>
        ))}
      </div>

      {/* Ads Table */}
      <div className="marketing-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Ad</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Platform</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Spent</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Impressions</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">CTR</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">ROAS</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAds.length > 0 ? filteredAds.map(ad => {
                const platform = getPlatformInfo(ad.platform);
                const ctr = ad.clicks && ad.impressions ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : 0;
                return (
                  <tr key={ad.id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{ad.name}</p>
                        <p className="text-xs text-muted-foreground">Budget: ${ad.budget || 0}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <platform.icon className={`w-4 h-4 ${platform.color}`} />
                        <span className="text-sm">{platform.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                        ad.status === 'active' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {ad.status || 'draft'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">${ad.spent || 0}</td>
                    <td className="py-3 px-4 text-right">{((ad.impressions || 0) / 1000).toFixed(0)}K</td>
                    <td className="py-3 px-4 text-right">
                      <span className={ctr > 3 ? 'text-emerald-400' : 'text-muted-foreground'}>
                        {ctr}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={(ad.roas || 0) > 4 ? 'text-emerald-400' : 'text-primary'}>
                        {ad.roas || '-'}x
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <button 
                          onClick={() => toggleAdStatus(ad)}
                          className="p-2 hover:bg-secondary rounded-lg transition-colors"
                        >
                          {ad.status === 'active' ? (
                            <Pause className="w-4 h-4 text-amber-400" />
                          ) : (
                            <Play className="w-4 h-4 text-emerald-400" />
                          )}
                        </button>
                        <div className="relative">
                          <button 
                            onClick={() => setShowActionsMenu(showActionsMenu === ad.id ? null : ad.id)}
                            className="p-2 hover:bg-secondary rounded-lg transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-muted-foreground" />
                          </button>
                          {showActionsMenu === ad.id && (
                            <div className="absolute right-0 top-full mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-50 py-1">
                              <button className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary transition-colors">
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteAd(ad.id)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="8" className="py-12 text-center text-muted-foreground">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No ads found</p>
                    <button onClick={() => setShowCreateModal(true)} className="marketing-btn-primary mt-4">
                      Create Your First Ad
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Performance Tips */}
      <div className="marketing-card">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          AI Optimization Tips
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-secondary/50 rounded-lg">
            <p className="text-sm font-medium text-emerald-400 mb-1">High Performance</p>
            <p className="text-sm text-muted-foreground">
              "B2B Lead Gen - Sponsored" has the highest ROAS at 5.2x. Consider increasing budget by 20%.
            </p>
          </div>
          <div className="p-4 bg-secondary/50 rounded-lg">
            <p className="text-sm font-medium text-amber-400 mb-1">Needs Attention</p>
            <p className="text-sm text-muted-foreground">
              "Brand Awareness - Video" has below-average CTR. Try A/B testing different thumbnails.
            </p>
          </div>
        </div>
      </div>

      {/* Create Ad Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-lg p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Create New Ad</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-secondary rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Ad Name *</label>
                <input
                  type="text"
                  value={newAd.name}
                  onChange={(e) => setNewAd({...newAd, name: e.target.value})}
                  placeholder="e.g., Summer Sale Campaign"
                  className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Platform</label>
                  <select
                    value={newAd.platform}
                    onChange={(e) => setNewAd({...newAd, platform: e.target.value})}
                    className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    {platforms.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Ad Type</label>
                  <select
                    value={newAd.type}
                    onChange={(e) => setNewAd({...newAd, type: e.target.value})}
                    className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="image">Image Ad</option>
                    <option value="video">Video Ad</option>
                    <option value="carousel">Carousel Ad</option>
                    <option value="text">Text Ad</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Headline *</label>
                <input
                  type="text"
                  value={newAd.headline}
                  onChange={(e) => setNewAd({...newAd, headline: e.target.value})}
                  placeholder="Attention-grabbing headline"
                  className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newAd.description}
                  onChange={(e) => setNewAd({...newAd, description: e.target.value})}
                  placeholder="Ad description..."
                  rows={3}
                  className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">CTA Button</label>
                  <select
                    value={newAd.cta}
                    onChange={(e) => setNewAd({...newAd, cta: e.target.value})}
                    className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="Learn More">Learn More</option>
                    <option value="Shop Now">Shop Now</option>
                    <option value="Sign Up">Sign Up</option>
                    <option value="Contact Us">Contact Us</option>
                    <option value="Get Quote">Get Quote</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Budget ($)</label>
                  <input
                    type="number"
                    value={newAd.budget}
                    onChange={(e) => setNewAd({...newAd, budget: e.target.value})}
                    placeholder="500"
                    className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Target URL</label>
                <input
                  type="url"
                  value={newAd.targetUrl}
                  onChange={(e) => setNewAd({...newAd, targetUrl: e.target.value})}
                  placeholder="https://yoursite.com/landing-page"
                  className="w-full bg-secondary border border-border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2.5 border border-border rounded-lg hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAd}
                disabled={saving}
                className="flex-1 marketing-btn-primary flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Create Ad
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
